import { useSelector } from 'react-redux';

import {
  getScanVaultLoginActivity,
  getScanVaultCVEDetails,
  getScanVaultResourceCountSummary,
  getScanVaultSecrets,
  ScanVaultLoginActivity,
} from 'api/scan-vault/client';
import { LegacyScanAnalysis } from 'api/services/legacyScanAnalysis';
import { useFlags } from 'app/flagsmith';
import {
  selectActiveCloudAccount,
  selectActiveEnvironment,
} from 'containers/App/selectors';
import { useAriksaQuery } from 'utils/query/useAriksaQuery';

import {
  DefaultApiGetCvesByIdRequest,
  ItemApiGetInstanceCountSummaryRequest,
  ItemApiGetInstanceScanByIdRequest,
  ItemApiGetInstanceSummaryByIdRequest,
} from './legacyRequestTypes';
import {
  InstanceSummary,
  LoginActivityType,
  ScanTypes,
  SummaryType,
  VulnerabilityCvePage,
} from './types';

export function useVulnerabilityResourceActivity(
  accountId: string,
  instanceId: string,
  activityType: LoginActivityType,
  resourceUUID?: string,
) {
  const { new_vuln_dashboard: vulnerabilityV3 } = useFlags([
    'new_vuln_dashboard',
  ]);
  const instanceTechnologyById = useAriksaQuery<any, ScanVaultLoginActivity[]>({
    queryKey: [
      instanceId,
      accountId,
      resourceUUID,
      activityType,
      vulnerabilityV3,
    ],
    fetcher: () =>
      vulnerabilityV3
        ? getScanVaultLoginActivity(resourceUUID ?? instanceId, activityType)
        : LegacyScanAnalysis.Item.getInstanceActivityById({
            instanceId,
            accountId,
            activityType,
          }).then(r => r.data),
  });
  return { instanceTechnologyById };
}

export function useVulnerabilityResourceScan(
  instanceId: string,
  scanType: ScanTypes,
  resourceUUID?: string,
) {
  const { new_vuln_dashboard: vulnerabilityV3 } = useFlags([
    'new_vuln_dashboard',
  ]);
  const instanceScanById = useAriksaQuery<
    ItemApiGetInstanceScanByIdRequest,
    Record<string, any>
  >({
    queryKey: [instanceId, resourceUUID, scanType, vulnerabilityV3],
    queryFn: () =>
      vulnerabilityV3 && resourceUUID && scanType === ScanTypes.Secrets
        ? getScanVaultSecrets(resourceUUID)
        : LegacyScanAnalysis.Item.getInstanceScanById({
            instanceId,
            scanType,
          }).then(r => r.data),
  });
  return { instanceScanById };
}

export function useVulnerabilityResourceSummary(summaryType: SummaryType) {
  const { new_vuln_dashboard: vulnerabilityV3 } = useFlags([
    'new_vuln_dashboard',
  ]);
  const { environmentId } = useSelector(selectActiveEnvironment);
  const { accountId } = useSelector(selectActiveCloudAccount);
  const ec2Summary = useAriksaQuery<
    ItemApiGetInstanceCountSummaryRequest,
    Record<string, any>
  >({
    queryKey: [environmentId, accountId, summaryType, vulnerabilityV3],
    queryFn: () =>
      vulnerabilityV3
        ? getScanVaultResourceCountSummary({
            environmentId,
            accountIds: accountId ? [accountId] : [],
            resourceTypes: ['EC2Instance'],
            summaryType,
          })
        : LegacyScanAnalysis.Item.getInstanceCountSummary({
            environmentId,
            accountId: accountId ? [accountId] : [],
            summaryType,
          }).then(r => r.data),
  });
  return { ec2Summary };
}

export function useVulnerabilityResourceSummaryById() {
  const instanceScamSummary = useAriksaQuery<
    ItemApiGetInstanceSummaryByIdRequest,
    InstanceSummary
  >({
    queryKey: [],
    fetcher: req =>
      LegacyScanAnalysis.Item.getInstanceSummaryById(req).then(r => r.data),
  });
  return { instanceScamSummary };
}

export function useVulnerabilityCvesById() {
  const { new_vuln_dashboard: vulnerabilityV3 } = useFlags([
    'new_vuln_dashboard',
  ]);
  const cvesById = useAriksaQuery<
    DefaultApiGetCvesByIdRequest,
    VulnerabilityCvePage
  >({
    queryKey: [],
    fetcher: req => {
      if (vulnerabilityV3) {
        const request = (req as any)?.cVEByIDRequest ?? {};
        return getScanVaultCVEDetails({
          cveIds: request.vulnerability_ids,
          packageName: request.package_name,
          packageVersion: request.package_version,
          search: (req as any)?.searchTerm,
          severity: (req as any)?.severity,
          status: (req as any)?.resolved ? 'resolved' : 'open',
        }) as Promise<VulnerabilityCvePage>;
      }
      return LegacyScanAnalysis.Default.getCvesById(req).then(r => r.data);
    },
  });
  return { cvesById };
}
